import React, { useCallback, useEffect, useRef, useState } from "react";
import { ThreeEvent, useThree } from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import { FloorplannerStoreContext } from "../../../../../store/floorplannerStore";
import {
  SlidingDoorType,
  SymbolType,
} from "../../../../../types/floorplannerTypes";
import { resolveMaterial } from "../../../materials/materialResolver";
import { shouldUseSymbolFillColorIn3D } from "../../../materials/primitiveSymbolFill";
import SelectedMeshOutline from "../../../scene/SelectedMeshOutline";
import { usePointerUpSelection } from "../../../scene/usePointerUpSelection";
import {
  Door3DAssemblyGeometry,
  Door3DLeafGeometry,
  getSymbolColor,
} from "../openingGeometry";
import { CANVAS_ACCENT_COLOR_HEX } from "../../../../../constants/brandColors";
import { resolveFillOpacity } from "../../../../../utils/fillOpacity";
import { canonicalizeFillPattern } from "../../../../../utils/fillPatterns";
import { getOpacityRemountEpoch } from "../../../../../utils/opacityRemount";
import DoorHandle3D from "./DoorHandle3D";
import DoorLeaf3D from "./DoorLeaf3D";
import DoorSlotMaterial from "./DoorSlotMaterial";
import {
  resolveDoorHandleStyle,
  resolveDoorStyle,
} from "./door3DStyle";
import { placeDoorHandleCenter } from "./doorPaneledLayout";

const SlidingDoor3DAssemblyMesh: React.FC<{
  symbol: SlidingDoorType;
  assembly: Door3DAssemblyGeometry;
  selected: boolean;
  claimSelectionEvents?: boolean;
  onSelect: (symbol: SymbolType, event: ThreeEvent<PointerEvent>) => void;
  onAngleDragActiveChange: (active: boolean) => void;
}> = observer(
  ({
    symbol,
    assembly,
    selected,
    claimSelectionEvents = false,
    onSelect,
    onAngleDragActiveChange,
  }) => {
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const { gl, invalidate } = useThree();
    const [draggingLeafId, setDraggingLeafId] = useState<string | null>(null);
    const draggingLeafIdRef = useRef<string | null>(null);

    // Sliding doors store open fraction in openAngle (0 = closed, 1 = fully open).
    const dragStartAmountRef = useRef(symbol.openAngle ?? 0);
    const latestAmountRef = useRef(symbol.openAngle ?? 0);
    const changedAmountRef = useRef(false);
    const leafGroupsRef = useRef(new Map<string, THREE.Group>());

    const selectionHandlers = usePointerUpSelection(symbol, onSelect, {
      claimPointerEvents: claimSelectionEvents,
    });

    useEffect(() => {
      draggingLeafIdRef.current = draggingLeafId;
    }, [draggingLeafId]);

    useEffect(() => {
      latestAmountRef.current = symbol.openAngle ?? 0;
      applySlideAmountToLeafGroups(
        assembly.leaves,
        leafGroupsRef.current,
        symbol.openAngle ?? 0,
        Boolean(symbol.flipVertical),
        assembly.wallWidth,
      );
    }, [symbol.openAngle, symbol.flipVertical, assembly.wallWidth]);

    useEffect(() => {
      applySlideAmountToLeafGroups(
        assembly.leaves,
        leafGroupsRef.current,
        latestAmountRef.current,
        Boolean(symbol.flipVertical),
        assembly.wallWidth,
      );
    }, [assembly.leaves, assembly.wallWidth, symbol.flipVertical]);

    const completeSlideDrag = useCallback(
      (updateState = true) => {
        if (!draggingLeafIdRef.current) return;

        draggingLeafIdRef.current = null;
        if (updateState) {
          setDraggingLeafId(null);
        }
        onAngleDragActiveChange(false);
        gl.domElement.style.cursor = "default";

        if (
          changedAmountRef.current &&
          Math.abs(latestAmountRef.current - dragStartAmountRef.current) > 1e-5
        ) {
          floorplannerStore.updateSymbolProperty(
            symbol.id,
            "openAngle",
            latestAmountRef.current,
          );
          floorplannerStore.recordSymbolChange(symbol.id, "modify");
          floorplannerStore.pushToUndoStack();
          floorplannerStore.setBlockDirty(false);
          floorplannerStore.setDirty();
        } else {
          floorplannerStore.cancelUndoTransaction();
          floorplannerStore.setBlockDirty(false);
        }
        invalidate();
      },
      [
        floorplannerStore,
        gl.domElement.style,
        invalidate,
        onAngleDragActiveChange,
        symbol.id,
      ],
    );

    const completeSlideDragRef = useRef(completeSlideDrag);
    useEffect(() => {
      completeSlideDragRef.current = completeSlideDrag;
    }, [completeSlideDrag]);

    useEffect(() => {
      if (!selected) completeSlideDrag();
    }, [completeSlideDrag, selected]);

    useEffect(
      () => () => {
        completeSlideDragRef.current(false);
      },
      [],
    );

    const finishSlideDrag = useCallback(
      (event: ThreeEvent<PointerEvent>) => {
        if (!draggingLeafIdRef.current) return;

        event.stopPropagation();
        event.nativeEvent.preventDefault();
        event.nativeEvent.stopImmediatePropagation?.();
        const pointerTarget = event.target as unknown as {
          releasePointerCapture?: (pointerId: number) => void;
        };
        try {
          pointerTarget.releasePointerCapture?.(event.pointerId);
        } catch {
          // The browser may already release capture before lostpointercapture.
        }
        completeSlideDrag();
      },
      [completeSlideDrag],
    );

    const updateSlideAmountFromPointer = useCallback(
      (leaf: Door3DLeafGeometry, event: ThreeEvent<PointerEvent>) => {
        if (draggingLeafId !== leaf.id) return;

        event.stopPropagation();
        event.nativeEvent.preventDefault();
        event.nativeEvent.stopImmediatePropagation?.();
        const nextPoint = getPointerPointOnZPlane(
          event,
          leaf.angleHandleCenter.z,
        );
        if (!nextPoint) return;

        const nextAmount = getDoorSlideAmountFromPointerPoint(
          assembly,
          leaf,
          nextPoint,
        );
        if (Math.abs(nextAmount - latestAmountRef.current) <= 1e-4) return;

        changedAmountRef.current = true;
        latestAmountRef.current = nextAmount;
        applySlideAmountToLeafGroups(
          assembly.leaves,
          leafGroupsRef.current,
          nextAmount,
          Boolean(symbol.flipVertical),
          assembly.wallWidth,
        );
        invalidate();
      },
      [assembly, draggingLeafId, invalidate, symbol.flipVertical],
    );

    const startSlideDrag = useCallback(
      (leaf: Door3DLeafGeometry, event: ThreeEvent<PointerEvent>) => {
        event.stopPropagation();
        event.nativeEvent.preventDefault();
        event.nativeEvent.stopImmediatePropagation?.();
        const pointerTarget = event.target as unknown as {
          setPointerCapture?: (pointerId: number) => void;
        };
        pointerTarget.setPointerCapture?.(event.pointerId);
        dragStartAmountRef.current = symbol.openAngle ?? 0;
        latestAmountRef.current = symbol.openAngle ?? 0;
        changedAmountRef.current = false;
        floorplannerStore.beginUndoTransaction("updateSlideAmount3D");
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.setBlockDirty(true);
        draggingLeafIdRef.current = leaf.id;
        setDraggingLeafId(leaf.id);
        onAngleDragActiveChange(true);
        gl.domElement.style.cursor = "grabbing";
        invalidate();
      },
      [
        floorplannerStore,
        gl.domElement.style,
        invalidate,
        onAngleDragActiveChange,
        symbol.id,
        symbol.openAngle,
      ],
    );

    const panelColor = resolveMaterial("doorPanel", symbol, {
      defaultColor: getSymbolColor(symbol),
    }).color;
    const symbol3DOnly = { threeD: symbol.threeD };
    const symbolPartOwner = shouldUseSymbolFillColorIn3D(symbol)
      ? symbol
      : symbol3DOnly;
    const frameColor = resolveMaterial("doorFrame", symbolPartOwner, {
      defaultColor: panelColor.clone().lerp(new THREE.Color(0xffffff), 0.38),
    }).color;
    const casingColor = frameColor.clone().lerp(panelColor, 0.45);
    const grooveColor = frameColor.clone().multiplyScalar(0.72);
    /** Glass leaf stiles follow Fill color (same as outer jamb). */
    const leafFrameColor = frameColor;
    /** Handles share beslag (doorHardware) with hinges and other metal fittings. */
    const solidHandleColor = resolveMaterial("doorHardware", symbolPartOwner, {
      defaultColor: 0xc9cdd1,
    }).color;
    const glassHandleColor = resolveMaterial("doorHardware", symbolPartOwner, {
      defaultColor: 0xdcdfe3,
    }).color;
    const fillOpacity = resolveFillOpacity(symbol);
    const opacityMaterialProps = {
      opacity: fillOpacity,
      transparent: fillOpacity < 1,
      depthWrite: fillOpacity >= 1,
    };
    /**
     * Remount when Fill opacity changes so Three.js materials discard stale
     * scene-fade state (same path as 2D doors and furniture 3D).
     */
    const opacityRemountEpoch = getOpacityRemountEpoch(symbol.id);
    /** Glass leaf is selected via Fill → Glass pattern. */
    const useGlassLeaf =
      canonicalizeFillPattern(symbol.fillPattern) === "glass" ||
      symbol.libraryKey === "MANUAL/GlassSlidingDoor";
    /**
     * Keep the pane as default glass — do not apply symbol fillColor, or the
     * glass tints with the frame (e.g. red fill → red glass). Fill only drives
     * the outer jamb and leaf stiles via frameColor / leafFrameColor.
     */
    const glassColor = resolveMaterial("windowGlass", symbol3DOnly, {
      defaultColor: 0xd2e8f5,
    }).color;
    const handleColor = useGlassLeaf ? glassHandleColor : solidHandleColor;
    const doorStyle = useGlassLeaf
      ? "glazed"
      : resolveDoorStyle(symbol.threeD?.doorStyle);
    /**
     * Unset solid sliding → knob (closest to main's flush disk pull).
     * Unset glass sliding → bar (same as main's glass pull).
     */
    const doorHandleStyle = resolveDoorHandleStyle(
      symbol.threeD?.doorHandleStyle,
      { fallback: useGlassLeaf ? "bar" : "knob" },
    );

    return (
      <group
        key={`opacity-remount-${opacityRemountEpoch}`}
        position={[assembly.origin.x, assembly.origin.y, 0]}
        rotation={[0, 0, assembly.rotation]}
        userData={{ floorplannerType: symbol.type, floorplannerId: symbol.id }}
        {...selectionHandlers}
        onPointerOver={() => {
          gl.domElement.style.cursor = "pointer";
        }}
        onPointerOut={() => {
          if (!draggingLeafId) gl.domElement.style.cursor = "default";
        }}
      >
        {assembly.frames.map((frame) => (
          <mesh
            key={frame.id}
            position={[frame.center.x, frame.center.y, frame.center.z]}
            rotation={[0, frame.rotationY ?? 0, 0]}
            castShadow
            receiveShadow
          >
            <boxGeometry args={[frame.size.x, frame.size.y, frame.size.z]} />
            <DoorSlotMaterial
              symbol={symbol}
              slot="doorFrame"
              fallbackColor={frameColor}
              fallbackRoughness={0.7}
              fallbackMetalness={0.05}
              {...opacityMaterialProps}
            />
            <SelectedMeshOutline visible={selected} />
          </mesh>
        ))}

        {assembly.stops.map((stop) => (
          <mesh
            key={stop.id}
            position={[stop.center.x, stop.center.y, stop.center.z]}
            rotation={[0, stop.rotationY ?? 0, 0]}
            castShadow
            receiveShadow
          >
            <boxGeometry args={[stop.size.x, stop.size.y, stop.size.z]} />
            <DoorSlotMaterial
              symbol={symbol}
              slot="doorFrame"
              fallbackColor={casingColor}
              fallbackRoughness={0.72}
              fallbackMetalness={0.02}
              {...opacityMaterialProps}
            />
            <SelectedMeshOutline visible={selected} />
          </mesh>
        ))}

        {assembly.casings.map((casing) => (
          <mesh
            key={casing.id}
            position={[casing.center.x, casing.center.y, casing.center.z]}
            rotation={[0, casing.rotationY ?? 0, 0]}
            castShadow
            receiveShadow
          >
            <boxGeometry args={[casing.size.x, casing.size.y, casing.size.z]} />
            <DoorSlotMaterial
              symbol={symbol}
              slot="doorFrame"
              fallbackColor={casingColor}
              fallbackRoughness={0.7}
              fallbackMetalness={0.02}
              {...opacityMaterialProps}
            />
            <SelectedMeshOutline visible={selected} />
          </mesh>
        ))}

        {assembly.miterGrooves.map((groove) => (
          <mesh
            key={groove.id}
            position={[groove.center.x, groove.center.y, groove.center.z]}
            rotation={[0, groove.rotationY ?? 0, 0]}
            castShadow
          >
            <boxGeometry args={[groove.size.x, groove.size.y, groove.size.z]} />
            <DoorSlotMaterial
              symbol={symbol}
              slot="doorFrame"
              fallbackColor={grooveColor}
              fallbackRoughness={0.78}
              fallbackMetalness={0}
              {...opacityMaterialProps}
            />
          </mesh>
        ))}

        {assembly.leaves.map((leaf) => {
          // Mount the leaf on the wall face (outside the opening plane).
          const wallFaceYOffset =
            (assembly.wallWidth / 2 + leaf.thickness / 2 + 0.01) *
            (symbol.flipVertical ? -1 : 1);

          return (
            <group
              key={leaf.id}
              ref={(group) => {
                if (group) {
                  leafGroupsRef.current.set(leaf.id, group);
                } else {
                  leafGroupsRef.current.delete(leaf.id);
                }
              }}
              position={[leaf.hinge.x, leaf.hinge.y + wallFaceYOffset, 0]}
              // Keep the leaf parallel to the wall; slide is translation only.
              rotation={[0, 0, 0]}
            >
              <DoorLeaf3D
                symbol={symbol}
                style={doorStyle}
                width={leaf.width}
                thickness={leaf.thickness}
                height={leaf.height}
                center={leaf.panelCenter}
                panelColor={panelColor}
                frameColor={leafFrameColor}
                glassColor={glassColor}
                fillOpacity={fillOpacity}
                opacityMaterialProps={opacityMaterialProps}
                selected={selected}
                flattenY
              />

              {/* One handle per leaf face (+Y / -Y); do not pin both to the wall-outer side. */}
              {leaf.handleCenters.map((handleCenter, index) => (
                <DoorHandle3D
                  key={`${leaf.id}-handle-${index}`}
                  symbol={symbol}
                  style={doorHandleStyle}
                  handleCenter={placeDoorHandleCenter(
                    doorStyle,
                    leaf,
                    handleCenter,
                  )}
                  leafWidth={leaf.width}
                  leafThickness={leaf.thickness}
                  leafHeight={leaf.height}
                  faceSide={handleCenter.y >= 0 ? 1 : -1}
                  useThicknessFace
                  handleColor={handleColor}
                  opacityMaterialProps={opacityMaterialProps}
                  selected={selected}
                />
              ))}

              {selected && (
                <group
                  position={[
                    leaf.angleHandleCenter.x,
                    0,
                    leaf.angleHandleCenter.z,
                  ]}
                  onPointerDown={(event) => startSlideDrag(leaf, event)}
                  onPointerMove={(event) =>
                    updateSlideAmountFromPointer(leaf, event)
                  }
                  onPointerUp={finishSlideDrag}
                  onPointerCancel={finishSlideDrag}
                  onLostPointerCapture={finishSlideDrag}
                  onPointerOver={(event) => {
                    event.stopPropagation();
                    gl.domElement.style.cursor = draggingLeafId
                      ? "grabbing"
                      : "grab";
                  }}
                  onPointerOut={(event) => {
                    event.stopPropagation();
                    if (!draggingLeafId) gl.domElement.style.cursor = "pointer";
                  }}
                >
                  <mesh>
                    <sphereGeometry args={[0.065, 20, 16]} />
                    <meshBasicMaterial color={CANVAS_ACCENT_COLOR_HEX} />
                  </mesh>
                  <mesh>
                    <sphereGeometry args={[0.12, 12, 10]} />
                    <meshBasicMaterial
                      transparent
                      opacity={0}
                      depthWrite={false}
                    />
                  </mesh>
                </group>
              )}
            </group>
          );
        })}
      </group>
    );
  },
);

function getPointerPointOnZPlane(
  event: ThreeEvent<PointerEvent>,
  z: number,
): THREE.Vector3 | null {
  return event.ray.intersectPlane(
    new THREE.Plane(new THREE.Vector3(0, 0, 1), -z),
    new THREE.Vector3(),
  );
}

function getDoorSlideAmountFromPointerPoint(
  assembly: Door3DAssemblyGeometry,
  leaf: Door3DLeafGeometry,
  point: THREE.Vector3,
): number {
  if (leaf.width === 0) return 0;

  const localPoint = new THREE.Vector2(point.x, point.y)
    .sub(assembly.origin)
    .rotateAround(new THREE.Vector2(0, 0), -assembly.rotation);

  const handleBaseX =
    leaf.angleHandleCenter.x * Math.cos(leaf.rotation) -
    leaf.angleHandleCenter.y * Math.sin(leaf.rotation);
  const startX = leaf.hinge.x + handleBaseX;

  // The door slides across the double-wide opening (leaf beside clear passage).
  const slideDir = leaf.closedSign;

  // Measure how far the mouse has traveled along X relative to the starting handle point
  const distance = localPoint.x - startX;
  const fraction = distance / (leaf.width * slideDir);

  // Clamp between closed (0) and fully open (1)
  return Math.max(0, Math.min(1, fraction));
}

function applySlideAmountToLeafGroups(
  leaves: Door3DLeafGeometry[],
  leafGroups: Map<string, THREE.Group>,
  slideAmount: number,
  flipVertical: boolean,
  wallWidth: number,
): void {
  const fraction = Math.max(0, Math.min(1, slideAmount));

  leaves.forEach((leaf) => {
    const group = leafGroups.get(leaf.id);
    if (!group) return;

    // Positive closedSign slides the leaf toward +X (over the open half).
    const slideDir = leaf.closedSign;
    const wallFaceYOffset =
      (wallWidth / 2 + leaf.thickness / 2 + 0.01) * (flipVertical ? -1 : 1);

    group.position.set(
      leaf.hinge.x + fraction * leaf.width * slideDir,
      leaf.hinge.y + wallFaceYOffset,
      0,
    );
    group.updateMatrixWorld();
  });
}

export default SlidingDoor3DAssemblyMesh;
